import { useState, useEffect } from 'react';
import isUndefined from 'lodash-es/isUndefined';
import { flow, pipe } from 'fp-ts/function';
import * as Either from 'fp-ts/Either';
import * as RemoteData from '../RemoteData';
import * as ErrorAsync from '../ErrorAsync';
import { makeCancellable } from '../Promises';
/**
 * A hook to handle the state of some asynchronous data retreiving operation
 * with RemoteData.
 *
 * @example
 *
 * ```tsx
 * import * as Either from 'fp-ts/Either';
 *
 * import { Http, RemoteData, useRemoteData } from '@catawiki/runtime-toolkit';
 *
 * const fetchUser = () => Promise.resolve(Either.right({ name: 'Jane Doe' }));
 *
 * const MyComponent = () => {
 *   const [data, setData] = useRemoteData(fetchUser, 'fetch_user');
 *
 *   return (
 *     <div>
 *       {RemoteData.match({
 *         idle: () => null,
 *         loading: () => <span>loading ...</span>,
 *         success: ({ name }) => <div>Name: {name}</div>,
 *         failure: error => (
 *           <span>Something went wrong: {JSON.stringify(error, null, 2)}</span>
 *         ),
 *       })(data)}
 *     </div>
 *   );
 * };
 * ```
 *
 * **Controlling fetch & re-fetch**
 *
 * `useRemoteData` triggers a re-fetch when the fetching function and the
 * context change. To prevent unwanted re-fetching make sure to wrap the fetch
 * function in `useCallback` if it can change across renders.
 *
 * ```ts
 * const getUser = useCallback(() => getUserById(id), [id]);
 * const [data, setData] = useRemoteData(getUser, 'fetch_user');
 * ```
 *
 * Besides that, `useRemoteData` triggers a (re-)fetch when the data is on
 * Loading state:
 *
 * - The 3rd argument, `defaultValue`, can be used to prevent fetching on the
 *   initial render.
 * - `setData(RemoteData.loading)` can be used to trigger a re-fetch.
 *
 * In the following example, the initial request on render will be skipped and
 * the fetch will be triggered either on `setData(RemoteData.loading)` or when
 * `getUser` changes.
 *
 * ```tsx
 * const [name, setName] = useState('John');
 * const getUser = useCallback(() => getUserByName(name), [name]);
 * const [data, setData] = useRemoteData(getUser, 'fetch_user', RemoteData.idle);
 *
 * <button onClick={() => setData(RemoteData.loading)}>
 *   Click me to fetch
 * </button>
 *
 * <button onClick={() => setName('Jane')}>
 *   Set name to Jane
 * </button>
 * ```
 *
 * **Error reporting**
 *
 * The hook also notifies the errors on failure to Honeybadger with
 * [`ErrorAsync.notify`](/docs/modules/ErrorAsync.ts.md#notify).
 *
 * Note that `ErrorAsync.notify` only notifies `ErrorAsync.Decoding` and
 * `ErrorAsync.Unknown` cases, `ErrorAsync.Client` and `ErrorAsync.Server`
 * aren't notified because the server already logs those. If you need to notify
 * server or client errors make sure to only report those cases to avoid
 * duplicated logs sent to Honeybadger.
 *
 * @deprecated in favour of useQuery
 */
export const useRemoteData = (getData, notifyContext, defaultValue) => {
    const [data, setData] = useState(() => defaultValue ?? RemoteData.initialLoading);
    useEffect(() => {
        setData((currentData) => {
            const initialData = isUndefined(defaultValue)
                ? RemoteData.loading(RemoteData.toNullable(currentData) ?? undefined)
                : defaultValue;
            return RemoteData.isSuccess(currentData) ||
                RemoteData.isFailure(currentData)
                ? initialData
                : currentData;
        });
    }, [defaultValue, getData, notifyContext]);
    useEffect(() => {
        if (RemoteData.isLoading(data)) {
            const { cancel, promise } = makeCancellable(getData());
            promise
                .then(Either.fold((error) => {
                setData(RemoteData.failure(error));
                ErrorAsync.notify(notifyContext, error);
            }, flow(RemoteData.success, setData)))
                .catch((error) => {
                const wrappedError = ErrorAsync.unknown(error);
                setData(RemoteData.failure(wrappedError));
                ErrorAsync.notify(notifyContext, wrappedError);
            });
            return cancel;
        }
        return undefined;
    }, [data, getData, notifyContext]);
    return [data, setData];
};
/**
 * A hook helper that provides a handy way to run the callback once RemoteData lands
 * on the success state.
 *
 * @example
 *
 * ```tsx
 * import * as Either from 'fp-ts/Either';
 *
 * import { Http, useRemoteData, useRemoteDataSuccess } from '@catawiki/runtime-toolkit';
 *
 * const fetchUser = () => Promise.resolve(Either.right({ name: 'Jane Doe', state: 'deleted' }));
 *
 * const MyComponent = () => {
 *   const [user, setUser] = useRemoteData(fetchUser, 'fetch_user');
 *
 *   useRemoteDataSuccess(user, ({ state }) => {
 *       if (state === 'deleted') {
 *          location.assign('/logout')
 *       }
 *   });
 *
 *   return (
 *     <div>
 *       {RemoteData.fold({
 *         idle: () => null,
 *         loading: () => <span>loading ...</span>,
 *         success: ({ name }) => <div>Name: {name}</div>,
 *         failure: error => (
 *           <span>Something went wrong: {JSON.stringify(error, null, 2)}</span>
 *         ),
 *       })(user)}
 *     </div>
 *   );
 * };
 * ```
 *
 * @deprecated in favour of useQuery
 */
export const useRemoteDataSuccess = (remoteDataA, onSuccess) => {
    useEffect(() => {
        pipe(remoteDataA, RemoteData.matchSuccess(onSuccess));
    }, [remoteDataA, onSuccess]);
};
/**
 * A hook helper that provides a handy way to run the callback once RemoteData lands
 * on the failure state.
 *
 * @example
 *
 * ```tsx
 * import * as Either from 'fp-ts/Either';
 *
 * import { useRemoteData, useRemoteDataFailure, RemoteData } from '@catawiki/runtime-toolkit';
 *
 * const fetchUser = () => Promise.reject('Error message');
 *
 * const MyComponent = () => {
 *   const [user, setUser] = useRemoteData(fetchUser, 'fetch_user');
 *
 *   useRemoteDataFailure(user, (error) => {
 *      console.log(error);
 *   });
 *
 *   return (
 *     <div>
 *       {RemoteData.match({
 *         idle: () => null,
 *         loading: () => <span>loading ...</span>,
 *         success: () => <div>success</div>,
 *         failure: error => (
 *           <span>Something went wrong: {JSON.stringify(error, null, 2)}</span>
 *         ),
 *       })(user)}
 *     </div>
 *   );
 * };
 * ```
 *
 * @deprecated in favour of useQuery
 */
export const useRemoteDataFailure = (remoteDataA, onFailure) => {
    useEffect(() => {
        pipe(remoteDataA, RemoteData.matchFailure(onFailure));
    }, [remoteDataA, onFailure]);
};
/**
 * A hook helper that provides a handy way to run the callback once RemoteData lands
 * on either the success or the failure state.
 * @example
 *
 * ```tsx
 * import * as Either from 'fp-ts/Either';
 *
 * import { Http, useRemoteData, useRemoteDataFinally } from '@catawiki/runtime-toolkit';
 *
 * const fetchUser = () => Promise.resolve(Either.right({ name: 'Jane Doe', cards: [{ amount: 500 }, { amount: 1720 }] }));
 *
 * const MyComponent = () => {
 *   const [user] = useRemoteData(fetchUser, 'fetch_user');
 *   const [totalAmount, setTotalAmount] = useState(0);
 *
 *   useRemoteDataFinally(user, () => {
 *       alert('Something went wrong');
 *   }, (data) => {
 *      setTotalAmount(data.cards.reduce((acc, { amount }) => acc + amount, 0));
 *   });
 *
 *   return (
 *     <div>
 *       {RemoteData.fold({
 *         idle: () => null,
 *         loading: () => <span>loading ...</span>,
 *         success: ({ name }) => <div>Name: {name}, amount: {totalAmount}</div>,
 *         failure: error => (
 *           <span>Something went wrong: {JSON.stringify(error, null, 2)}</span>
 *         ),
 *       })(user)}
 *     </div>
 *   );
 * };
 * ```
 *
 * @deprecated in favour of useQuery
 */
export const useRemoteDataFinally = (remoteDataA, onFailure, onSuccess) => {
    useEffect(() => {
        pipe(remoteDataA, RemoteData.matchFinally(onFailure, onSuccess));
    }, [remoteDataA, onFailure, onSuccess]);
};
